import axios from 'axios';
import { LOCAL_API_BASE_URL } from './backendBaseUrls';

export const LEGACY_BACKEND_PROXY_PREFIX = '/backend';

const LOCAL_BROWSER_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

// Browser requests stay same-origin so local CRA proxy and Vercel rewrites can forward
// both `/api/*` and legacy bare backend routes.
export const resolveApiBaseUrl = ({
  hasWindow = typeof window !== 'undefined',
} = {}) => {
  if (hasWindow) {
    return '';
  }

  return process.env.REACT_APP_API_URL || LOCAL_API_BASE_URL;
};

// Browser navigations (iframes/window.open) do not use the CRA API proxy in local
// development, so they need an absolute backend URL on localhost.
export const resolveApiNavigationBaseUrl = ({
  hasWindow = typeof window !== 'undefined',
  hostname = hasWindow ? window.location.hostname : undefined,
} = {}) => {
  if (hasWindow) {
    if (LOCAL_BROWSER_HOSTNAMES.has(String(hostname || '').toLowerCase())) {
      return (process.env.REACT_APP_API_URL || LOCAL_API_BASE_URL).replace(/\/$/, '');
    }

    return '';
  }

  return (process.env.REACT_APP_API_URL || LOCAL_API_BASE_URL).replace(/\/$/, '');
};

export const prefixHostedLegacyBackendPath = (
  url,
  {
    hasWindow = typeof window !== 'undefined',
    hostname = hasWindow ? window.location.hostname : undefined,
  } = {}
) => {
  if (typeof url !== 'string' || !url.startsWith('/')) {
    return url;
  }

  if (!hasWindow) {
    return url;
  }

  if (LOCAL_BROWSER_HOSTNAMES.has(String(hostname || '').toLowerCase())) {
    return url;
  }

  if (
    url === '/api' ||
    url.startsWith('/api/') ||
    url === LEGACY_BACKEND_PROXY_PREFIX ||
    url.startsWith(`${LEGACY_BACKEND_PROXY_PREFIX}/`) ||
    url.startsWith('/backend-forms/')
  ) {
    return url;
  }

  return `${LEGACY_BACKEND_PROXY_PREFIX}${url}`;
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const { skipPortalToken, ...requestConfig } = config;
  const newConfig = {
    ...requestConfig,
    headers: requestConfig.headers || {},
    url: prefixHostedLegacyBackendPath(requestConfig.url),
  };
  
  const portalToken = !skipPortalToken && typeof localStorage !== 'undefined'
    ? localStorage.getItem('portal_token')
    : null;
  const hasAuthorizationHeader = Boolean(
    newConfig.headers.Authorization || newConfig.headers.authorization
  );
  if (portalToken && !hasAuthorizationHeader && !newConfig.headers['X-Portal-Token']) {
    newConfig.headers['X-Portal-Token'] = `Bearer ${portalToken}`;
  }
  
  return newConfig;
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default apiClient;
